import type { ReactNode } from 'react'
import type { ResponsiveImageType } from 'react-datocms'
import type { MetaDescriptor } from 'react-router'
import { buildSrcSetFromSrc } from '#src/components/image/srcImageUtils.js'

interface Props {
  responsiveImage: ResponsiveImageType
  className?: string
  sizes?: string[]
}

const srcSetCandidates = [0.5, 0.75, 1, 1.5, 2]

export function EagerDatoImage(props: Props): ReactNode {
  const { responsiveImage, sizes = ['100vw'] } = props

  if (!responsiveImage) return null

  const srcSet = buildSrcSetFromSrc(responsiveImage.src, responsiveImage.width, srcSetCandidates)

  return (
    <picture>
      <source srcSet={srcSet} sizes={sizes.join(',')} />
      <img
        className={props.className}
        src={responsiveImage.src!}
        alt={responsiveImage.alt || ''}
        title={responsiveImage.title || undefined}
        loading="eager"
        fetchPriority="high"
      />
    </picture>
  )
}

/**
 * A route `meta` descriptor that preloads the image an <EagerDatoImage> with
 * the same props renders. Put it in the meta of routes whose LCP element is
 * an EagerDatoImage (hero), so the browser starts downloading it from the
 * HTML head. Same srcset/sizes as the rendered <picture>, so the preload and
 * the element resolve to the same candidate and the fetch is shared.
 */
export function eagerDatoImagePreload(
  responsiveImage: ResponsiveImageType | undefined,
  sizes: string[],
): MetaDescriptor[] {
  const srcSet =
    responsiveImage &&
    buildSrcSetFromSrc(responsiveImage.src, responsiveImage.width, srcSetCandidates)
  if (!srcSet) return []
  return [
    {
      tagName: 'link',
      rel: 'preload',
      as: 'image',
      fetchPriority: 'high',
      imageSrcSet: srcSet,
      imageSizes: sizes.join(','),
    },
  ]
}
